// Registrar Factura de Compra (manual). Financiero → Compras y Pagos.
// Para la compra que NO está en el portal del fisco: importaciones, proveedores
// del exterior, comprobantes en papel. Graba con cxp_registrar_manual, que crea
// un documento sintético (origen='manual') + la cuenta por pagar; el trigger
// trg_contab de tickets_buy genera el asiento contable.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2, FilePlus, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import { labelCentroCosto, useCentrosContables, useCentroCostoModo } from './centroCosto';
import { SortableTh, useSort } from './tablaSort';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const usd = (v: number) => v.toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const n2 = (v: number) => (Number.isFinite(v) ? v : 0);

// Tarifas de IVA vigentes/históricas en Ecuador (tabla 16 del SRI).
const TARIFAS = [0, 5, 12, 14, 15];
const TIPOS = ['Factura', 'Liquidación de compra', 'Nota de Débito'];

interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
interface Linea {
  key: number; codigo: string; descripcion: string;
  cantidad: number; precio_unitario: number; descMode: 'pct' | 'fijo'; descVal: number; iva_tarifa: number;
}
interface Registrada {
  id: string; serie_comprobante: string; tipo_comprobante: string;
  fecha_emision: string; fecha_vencimiento: string | null;
  ruc_emisor: string; razon_social_emisor: string; categoria: string | null;
  subtotal: number; iva: number; importe_total: number; pagos: number;
}

let _k = 1;
const nuevaLinea = (): Linea => ({
  key: _k++, codigo: '', descripcion: '', cantidad: 1, precio_unitario: 0,
  descMode: 'pct', descVal: 0, iva_tarifa: 15,
});
/** Descuento en dinero de la línea completa (la RPC lo espera así, no por unidad). */
const descLinea = (l: Linea) => {
  const bruto = n2(l.cantidad) * n2(l.precio_unitario);
  return l.descMode === 'pct' ? bruto * n2(l.descVal) / 100 : n2(l.descVal);
};
const baseLinea = (l: Linea) => Math.max(n2(l.cantidad) * n2(l.precio_unitario) - descLinea(l), 0);
const ivaLinea = (l: Linea) => Math.round(baseLinea(l) * n2(l.iva_tarifa)) / 100;

export function RegistrarCompra() {
  const [vista, setVista] = useState<'nueva' | 'registradas'>('nueva');
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Registrar Factura de Compra</h1>
          <p className="text-sm text-muted-foreground">
            Para la compra que no llega por el portal del fisco: importaciones, proveedores del exterior, comprobantes en papel.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border p-1">
          {([['nueva', 'Nueva compra'], ['registradas', 'Registradas']] as const).map(([k, t]) => (
            <button key={k} onClick={() => setVista(k)}
              className={`rounded-md px-3 py-1 text-sm ${vista === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
      </div>
      {vista === 'nueva' ? <NuevaCompra onCreada={() => setVista('registradas')} /> : <Registradas />}
    </div>
  );
}

function NuevaCompra({ onCreada }: { onCreada: () => void }) {
  const qc = useQueryClient();
  const { data: partners = [] } = useQuery<PartnerOpt[]>({
    queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
  });
  const proveedores = useMemo(
    () => partners.filter((p) => (p.tipo_partner || []).includes('proveedor') && p.identificacion),
    [partners]);
  const centros = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('centro_costo')), [partners]);
  const { modo: modoCentro } = useCentroCostoModo();
  const { data: centrosContables = [] } = useCentrosContables(modoCentro === 'contable');
  const labelCentro = labelCentroCosto(modoCentro);
  const { data: categorias = [] } = useQuery<string[]>({
    queryKey: ['sri-categorias'], queryFn: async () => (await apiClient.post('/rpc/sri_categorias', {}, FIN)).data || [],
  });

  const [ident, setIdent] = useState('');
  const [razon, setRazon] = useState('');
  const [tipo, setTipo] = useState('Factura');
  const [serie, setSerie] = useState('');
  const [fecha, setFecha] = useState(fechaLocal());
  const [venc, setVenc] = useState('');
  const [categoria, setCategoria] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  const [centroId, setCentroId] = useState('');
  const [obs, setObs] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([nuevaLinea()]);

  const setLinea = (key: number, patch: Partial<Linea>) =>
    setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  // Al escribir la identificación de un proveedor conocido se completa la razón social.
  const alCambiarIdent = (v: string) => {
    setIdent(v);
    const p = proveedores.find((x) => x.identificacion === v.trim());
    if (p) setRazon(p.razon_social);
  };

  const subtotal = lineas.reduce((s, l) => s + baseLinea(l), 0);
  const descuento = lineas.reduce((s, l) => s + descLinea(l), 0);
  const iva = lineas.reduce((s, l) => s + ivaLinea(l), 0);
  const total = subtotal + iva;

  const guardar = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/cxp_registrar_manual', {
      _data: {
        identificacion: ident.trim(), razon_social: razon.trim(), tipo_comprobante: tipo,
        serie: serie.trim(), fecha_emision: fecha, fecha_vencimiento: venc || null,
        categoria, proyecto_id: proyectoId || null,
        centro_costo_id: modoCentro === 'contable' ? null : (centroId || null),
        cc_contable_id: modoCentro === 'contable' ? (centroId || null) : null,
        observaciones: obs.trim() || null,
        lineas: lineas.map((l) => ({
          codigo: l.codigo.trim() || null, descripcion: l.descripcion.trim(),
          cantidad: n2(l.cantidad), precio_unitario: n2(l.precio_unitario),
          descuento: Number(descLinea(l).toFixed(2)), iva_tarifa: n2(l.iva_tarifa),
        })),
      },
    }, FIN)).data,
    onSuccess: (r: { total: number; lineas: number }) => {
      toast.success(`Compra registrada por ${usd(Number(r.total))} (${r.lineas} línea(s))`);
      // El asiento, la cartera y el aging se alimentan de lo mismo: invalidar todo.
      void qc.invalidateQueries({ queryKey: ['cxp-manuales'] });
      void qc.invalidateQueries({ queryKey: ['cxp'] });
      void qc.invalidateQueries({ queryKey: ['fin-partners'] });
      onCreada();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const faltaCentro = !centroId;
  const puede = ident.trim() && razon.trim() && serie.trim() && fecha && categoria && !faltaCentro
    && lineas.length > 0 && total > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Comprobante</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Identificación del proveedor *</label>
            <input className={field} list="prov-ident" value={ident} maxLength={13}
              onChange={(e) => alCambiarIdent(e.target.value)} placeholder="RUC, cédula o ID tributaria" />
            <datalist id="prov-ident">
              {proveedores.map((p) => <option key={p.id} value={p.identificacion!}>{p.razon_social}</option>)}
            </datalist>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Razón social *</label>
            <input className={field} value={razon} onChange={(e) => setRazon(e.target.value)}
              placeholder="Nombre del proveedor" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Tipo de comprobante</label>
            <select className={field} value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Número del comprobante *</label>
            <input className={field} value={serie} onChange={(e) => setSerie(e.target.value)}
              placeholder="001-001-000000123" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Fecha de emisión *</label>
            <input type="date" className={field} value={fecha} max={fechaLocal()}
              onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Vence</label>
            <input type="date" className={field} value={venc} min={fecha}
              onChange={(e) => setVenc(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Categoría *</label>
            <select className={field} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">— Selecciona —</option>
              {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">{labelCentro} *</label>
            <select className={field} value={centroId} onChange={(e) => setCentroId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {modoCentro === 'contable'
                ? centrosContables.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)
                : centros.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Proyecto</label>
            <select className={field} value={proyectoId} onChange={(e) => setProyectoId(e.target.value)}>
              <option value="">—</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Observaciones</label>
            <input className={field} value={obs} onChange={(e) => setObs(e.target.value)}
              placeholder="Referencia, embarque, orden de compra…" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Detalle</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setLineas((ls) => [...ls, nuevaLinea()])}>
            <Plus className="w-4 h-4 mr-1" /> Línea
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3 text-left w-10">N°</th>
                <th className="py-2 pr-3 text-left w-28">Código</th>
                <th className="py-2 pr-3 text-left">Descripción</th>
                <th className="py-2 pr-3 text-right w-24">Cantidad</th>
                <th className="py-2 pr-3 text-right w-28">P. unitario</th>
                <th className="py-2 pr-3 text-right w-36">Descuento</th>
                <th className="py-2 pr-3 text-right w-24">IVA</th>
                <th className="py-2 pr-3 text-right w-28">Base</th>
                <th className="py-2 pr-3 text-right w-28">Total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="py-1 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1 pr-3">
                    <input className={field} value={l.codigo} onChange={(e) => setLinea(l.key, { codigo: e.target.value })} />
                  </td>
                  <td className="py-1 pr-3">
                    <input className={field} value={l.descripcion}
                      onChange={(e) => setLinea(l.key, { descripcion: e.target.value })} placeholder="Concepto" />
                  </td>
                  <td className="py-1 pr-3">
                    <input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.cantidad}
                      onChange={(e) => setLinea(l.key, { cantidad: Number(e.target.value) })} />
                  </td>
                  <td className="py-1 pr-3">
                    <input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.precio_unitario}
                      onChange={(e) => setLinea(l.key, { precio_unitario: Number(e.target.value) })} />
                  </td>
                  <td className="py-1 pr-3">
                    <div className="flex gap-1">
                      <input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.descVal}
                        onChange={(e) => setLinea(l.key, { descVal: Number(e.target.value) })} />
                      <select className={`${field} w-16`} value={l.descMode}
                        onChange={(e) => setLinea(l.key, { descMode: e.target.value as 'pct' | 'fijo' })}>
                        <option value="pct">%</option>
                        <option value="fijo">$</option>
                      </select>
                    </div>
                  </td>
                  <td className="py-1 pr-3">
                    <select className={`${field} text-right`} value={l.iva_tarifa}
                      onChange={(e) => setLinea(l.key, { iva_tarifa: Number(e.target.value) })}>
                      {TARIFAS.map((t) => <option key={t} value={t}>{t}%</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">{baseLinea(l).toFixed(2)}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{(baseLinea(l) + ivaLinea(l)).toFixed(2)}</td>
                  <td className="py-1">
                    <button className="text-muted-foreground hover:text-destructive disabled:opacity-30"
                      disabled={lineas.length === 1}
                      onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t font-medium">
              <tr>
                <td colSpan={7} className="py-2 pr-3 text-right text-muted-foreground">Subtotal</td>
                <td className="py-2 pr-3 text-right tabular-nums">{subtotal.toFixed(2)}</td>
                <td colSpan={2} />
              </tr>
              {descuento > 0 && (
                <tr>
                  <td colSpan={7} className="py-1 pr-3 text-right text-muted-foreground">Descuento</td>
                  <td className="py-1 pr-3 text-right tabular-nums">−{descuento.toFixed(2)}</td>
                  <td colSpan={2} />
                </tr>
              )}
              <tr>
                <td colSpan={7} className="py-1 pr-3 text-right text-muted-foreground">IVA</td>
                <td className="py-1 pr-3 text-right tabular-nums">{iva.toFixed(2)}</td>
                <td colSpan={2} />
              </tr>
              <tr className="text-base">
                <td colSpan={7} className="py-2 pr-3 text-right">Total</td>
                <td className="py-2 pr-3 text-right tabular-nums">{usd(total)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-4">
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="w-4 h-4 shrink-0 mt-px" />
          Al registrarla se crea la cuenta por pagar y su asiento contable. No se envía nada al fisco:
          esta compra no aparecerá en el portal, pero sí en Cartera por Pagar, la antigüedad de saldos y el ATS.
        </p>
        <Button disabled={!puede || guardar.isPending} onClick={() => guardar.mutate()}>
          {guardar.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FilePlus className="w-4 h-4 mr-1" />}
          Registrar compra
        </Button>
      </div>
    </div>
  );
}

function Registradas() {
  const qc = useQueryClient();
  const { data: filas = [], isLoading } = useQuery<Registrada[]>({
    queryKey: ['cxp-manuales'],
    queryFn: async () => (await apiClient.post('/rpc/cxp_registrar_manual_listar', { _limite: 200 }, FIN)).data || [],
  });
  const { sorted, sort, toggle } = useSort(filas);
  const borrar = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/cxp_registrar_manual_eliminar', { _ticket_id: id }, FIN)).data,
    onSuccess: () => {
      toast.success('Compra eliminada; su asiento contable quedó reversado');
      void qc.invalidateQueries({ queryKey: ['cxp-manuales'] });
      void qc.invalidateQueries({ queryKey: ['cxp'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const tot = filas.reduce((s, f) => ({
    subtotal: s.subtotal + Number(f.subtotal), iva: s.iva + Number(f.iva), total: s.total + Number(f.importe_total),
  }), { subtotal: 0, iva: 0, total: 0 });

  const dlg = useDialogoEnLinea();

  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <Card>
      {dlg.ui}
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Compras registradas a mano ({filas.length})</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {filas.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Todavía no has registrado ninguna compra a mano.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3 text-left w-10">N°</th>
                <SortableTh col="fecha_emision" label="Emisión" sort={sort} onSort={toggle} />
                <SortableTh col="fecha_vencimiento" label="Vence" sort={sort} onSort={toggle} />
                <SortableTh col="tipo_comprobante" label="Tipo" sort={sort} onSort={toggle} />
                <SortableTh col="serie_comprobante" label="Número" sort={sort} onSort={toggle} />
                <SortableTh col="razon_social_emisor" label="Proveedor" sort={sort} onSort={toggle} />
                <SortableTh col="categoria" label="Categoría" sort={sort} onSort={toggle} />
                <SortableTh col="subtotal" label="Subtotal" align="right" sort={sort} onSort={toggle} />
                <SortableTh col="iva" label="IVA" align="right" sort={sort} onSort={toggle} />
                <SortableTh col="importe_total" label="Total" align="right" sort={sort} onSort={toggle} />
                <SortableTh col="pagos" label="Pagos" align="center" sort={sort} onSort={toggle} />
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((f, i) => (
                <tr key={f.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{String(f.fecha_emision).slice(0, 10)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{f.fecha_vencimiento ? String(f.fecha_vencimiento).slice(0, 10) : '—'}</td>
                  <td className="py-2 pr-3">{f.tipo_comprobante}</td>
                  <td className="py-2 pr-3 font-medium">{f.serie_comprobante}</td>
                  <td className="py-2 pr-3">
                    {f.razon_social_emisor}
                    <span className="block text-xs text-muted-foreground">{f.ruc_emisor}</span>
                  </td>
                  <td className="py-2 pr-3">{f.categoria || '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{Number(f.subtotal).toFixed(2)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{Number(f.iva).toFixed(2)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium">{Number(f.importe_total).toFixed(2)}</td>
                  <td className="py-2 pr-3 text-center">{f.pagos > 0 ? f.pagos : '—'}</td>
                  <td className="py-2">
                    <button
                      title={f.pagos > 0 ? 'Tiene pagos aplicados: reversa los pagos antes de eliminarla' : 'Eliminar'}
                      className="text-muted-foreground hover:text-destructive disabled:opacity-30"
                      disabled={f.pagos > 0 || borrar.isPending}
                      onClick={async () => {
                        if (await dlg.confirmar(`¿Eliminar la compra ${f.serie_comprobante} de ${f.razon_social_emisor}?`, { detalle: 'Se reversa también su asiento contable.', peligro: true })) borrar.mutate(f.id);
                      }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t font-medium">
              <tr>
                <td colSpan={7} className="py-2 pr-3 text-right text-muted-foreground">Totales</td>
                <td className="py-2 pr-3 text-right tabular-nums">{tot.subtotal.toFixed(2)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{tot.iva.toFixed(2)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{tot.total.toFixed(2)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

export default RegistrarCompra;
